import {
  ActionIcon,
  Box,
  Breadcrumbs,
  Button,
  Col,
  Container,
  Flex,
  Grid,
  Image,
  Modal,
  Paper,
  Rating,
  Table,
  Tabs,
  Text,
  Textarea,
  useMantineTheme,
  createStyles,
  Badge,
  Transition,
  Group,
  Divider,
  ThemeIcon,
} from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconAlignJustified,
  IconCash,
  IconChevronDown,
  IconHeart,
  IconList,
  IconLock,
  IconMinus,
  IconPlus,
  IconShare,
  IconShoppingCartPlus,
  IconStars,
  IconTruckDelivery,
  IconArrowLeft,
  IconClock,
  IconInfoCircle,
} from '@tabler/icons-react';
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../services/master.service';
import { image_url, is_mobile_view } from '../../config';
import { showErrorToast, showSuccessToast } from '../../utilities/Toast';
import { Input, Rate } from 'antd';
import { review } from '../../services/website.service';
import { createAlert } from '../../components/Notification';
import { CartContext } from '../../App';
import { priceCalculator, qtyCalculator } from '../../utilities/Common';
import { motion, AnimatePresence } from 'framer-motion';

const useStyles = createStyles((theme) => ({
  root: {
    paddingTop: 180,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[0],
    minHeight: '100vh',
    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
      paddingTop: 150,
    },
  },

  imageCard: {
    borderRadius: theme.radius.lg,
    overflow: 'hidden',
    backgroundColor: theme.white,
    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
      borderRadius: 0,
    },
  },

  mobileHeaderActions: {
    position: 'absolute',
    top: 20,
    left: 20,
    right: 20,
    zIndex: 10,
    display: 'flex',
    justifyContent: 'space-between',
  },

  actionButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(8px)',
    borderRadius: '50%',
    boxShadow: theme.shadows.sm,
    '&:hover': {
      backgroundColor: theme.white,
    },
  },

  infoCard: {
    padding: theme.spacing.xl,
    borderRadius: theme.radius.lg,
    backgroundColor: theme.white,
    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
      padding: theme.spacing.md,
      borderRadius: 0,
      borderTop: `1px solid ${theme.colors.gray[2]}`,
    },
  },

  priceSection: {
    padding: theme.spacing.md,
    borderRadius: theme.radius.md,
    backgroundColor: theme.colors.brand[0],
    border: `1px solid ${theme.colors.brand[2]}`,
  },

  batchBreakdown: {
    marginTop: theme.spacing.md,
    padding: theme.spacing.md,
    borderRadius: theme.radius.md,
    backgroundColor: theme.colors.gray[0],
    border: `1px dashed ${theme.colors.brand[4]}`,
  },

  cartRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 24,
  },

  stickyBottom: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '16px 20px',
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(16px)',
    borderTop: `1px solid ${theme.colors.gray[2]}`,
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    boxShadow: '0 -4px 20px rgba(0,0,0,0.05)',
    paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
  },

  mobileStickyQty: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.colors.gray[1],
    borderRadius: theme.radius.md,
    padding: 4,
  },

  qtySelector: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1],
    borderRadius: 40,
    padding: '4px 8px',
    border: `1.5px solid ${theme.colors.gray[3]}`,
    gap: 4,
  },

  qtyBtn: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: 'none',
    backgroundColor: theme.white,
    boxShadow: theme.shadows.xs,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: theme.colors.brand[0],
      boxShadow: theme.shadows.sm,
    },
    '&:disabled': {
      opacity: 0.4,
      cursor: 'not-allowed',
    },
  },

  addToCartBtn: {
    flex: 1,
    height: 52,
    borderRadius: 14,
    fontWeight: 700,
    fontSize: 16,
    background: `linear-gradient(135deg, ${theme.colors.brand[6]}, ${theme.colors.brand[8]})`,
    border: 'none',
    color: theme.white,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    boxShadow: `0 4px 18px ${theme.colors.brand[3]}88`,
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 24px ${theme.colors.brand[4]}99`,
    },
    '&:disabled': {
      background: theme.colors.gray[4],
      boxShadow: 'none',
      cursor: 'not-allowed',
      transform: 'none',
    },
  },

  variantChip: {
    padding: '8px 16px',
    borderRadius: theme.radius.md,
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: `2px solid transparent`,
    '&:hover': {
      backgroundColor: theme.colors.gray[1],
    },
  },

  selectedVariant: {
    border: `2px solid ${theme.colors.brand[5]}`,
    backgroundColor: theme.colors.brand[0],
    color: theme.colors.brand[7],
  },
}));

function ProductView() {
  const { classes, cx } = useStyles();
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const [searchParams] = useSearchParams();
  const { setCartItemCount } = useContext(CartContext);

  let isMobileScreen = useMediaQuery('(max-width: 768px)');
  isMobileScreen = is_mobile_view ? is_mobile_view : isMobileScreen;

  const [product, setProduct] = useState(null);
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [specifications, setSpecifications] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [stars, setStars] = useState(2.5);
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [selectedVarient, setSelectedVarient] = useState(null);

  const getInitialVariant = (prod) => {
    if (prod?.isVarient && Array.isArray(prod?.variants) && prod.variants.length > 0) {
      const firstVariant = Number(prod.variants[0]);
      return isNaN(firstVariant) ? 1 : firstVariant;
    }
    return 1;
  };

  const calculateBatchBreakdown = (qty) => {
    if (!product?.active_batches || product.active_batches.length === 0) return [];
    const totalBaseQty = qtyCalculator(qty, selectedVarient, product?.varientUnit, product?.isVarient);
    let remaining = totalBaseQty;
    const breakdown = [];
    
    for (const batch of product.active_batches) {
      if (remaining <= 0) break;
      const stock = Number(batch.stock);
      const take = Math.min(remaining, stock);
      if (take > 0) {
        breakdown.push({
          ...batch,
          appliedQty: take,
          displayQty: take / (qtyCalculator(1, selectedVarient, product?.varientUnit, product?.isVarient))
        });
        remaining -= take;
      }
    }
    
    if (remaining > 0) {
        const lastBatch = product.active_batches[product.active_batches.length - 1];
        breakdown.push({
            batch_no: 'Additional',
            selling_price: lastBatch?.selling_price || product.selling_price,
            mrp: lastBatch?.mrp || product.mrp,
            appliedQty: remaining,
            displayQty: remaining / (qtyCalculator(1, selectedVarient, product?.varientUnit, product?.isVarient))
        });
    }
    return breakdown;
  };

  const batchBreakdown = useMemo(() => calculateBatchBreakdown(quantity), [product, quantity, selectedVarient]);
  const totalPrice = useMemo(() => batchBreakdown.reduce((sum, item) => sum + (item.appliedQty * item.selling_price), 0), [batchBreakdown]);
  const totalMrp = useMemo(() => batchBreakdown.reduce((sum, item) => sum + (item.appliedQty * item.mrp), 0), [batchBreakdown]);

  useEffect(() => {
    if (product) setSelectedVarient(getInitialVariant(product));
  }, [product]);

  const varientOptions = useMemo(() => {
    if (product?.isVarient && Array.isArray(product?.variants)) {
      return [...product.variants].sort((a, b) => Number(a) - Number(b)).map(v => Number(v));
    }
    return [];
  }, [product]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get_product(searchParams.get('id')).then((res) => {
      const data = res.data.data;
      setProduct(data);
      if (data.specification) setSpecifications(JSON.parse(data.specification));
      
      if (data.product_image?.length > 0) {
        const imgList = data.product_image.map(e => ({ id: e.id, image: image_url + e.image }));
        setImages(imgList);
        setSelectedImage(imgList[0].image);
      } else {
        const placeholder = 'https://d23qowwaqkh3fj.cloudfront.net/wp-content/uploads/2022/01/placeholder-277.png';
        setImages([{ id: 0, image: placeholder }]);
        setSelectedImage(placeholder);
      }
    });
  }, [searchParams]);

  const addToCart = async () => {
    const res = await api.add_to_cart({
      product_id: product?.id,
      quantity: qtyCalculator(quantity, selectedVarient, product?.varientUnit, product?.isVarient),
      varientSlug: `${selectedVarient}${product?.isVarient ? product?.varientUnit : 'pkt'} x ${quantity}`
    });

    if (res.data.success) {
      const data = res.data.data;
      const productRows = data.cartitem?.filter(item => item.bundle === null).length ?? 0;
      const bundleRows = data.cart_bundles?.length ?? 0;
      setCartItemCount(productRows + bundleRows);
      showSuccessToast({ title: 'Success', message: 'Item added to cart.' });
    } else if (res.data.code === 401) {
      navigate('/login');
      showErrorToast({ title: 'Warning', message: 'Please login to continue.' });
    } else {
      showErrorToast({ title: 'Error', message: res.data.message });
    }
  };

  const submitReview = () => {
    if (reviewText && reviewTitle) {
      review.add({
        product_id: searchParams.get('id'),
        title: reviewTitle,
        description: reviewText,
        star: stars,
      }).then((res) => {
        if (res.success) {
          createAlert('success', 'Review added!');
          setIsModalVisible(false);
        } else {
          createAlert('error', res.message);
        }
      });
    } else {
      createAlert('error', 'Details missing');
    }
  };

  const outOfStock = (product?.stock_qty - (product?.ordered_qty || 0)) <= (product?.min_qty || 0) || product?.stock_qty <= 0 || !product?.is_batch;

  const freshDays = useMemo(() => {
    if (!product?.batch_exp_date) return null;
    const expiry = new Date(product.batch_exp_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  }, [product]);

  if (!product) return null;

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }}
      className={classes.root}
    >
      <Container size="xl" px={isMobileScreen ? 0 : 'md'}>
        <Box mb={24} px={isMobileScreen ? 'md' : 0} pt={isMobileScreen ? 'md' : 0}>
          <Breadcrumbs separator="/">
            <Link to="/" style={{ textDecoration: 'none', color: theme.colors.gray[6], fontSize: 14 }}>Home</Link>
            <Link to="/products" style={{ textDecoration: 'none', color: theme.colors.gray[6], fontSize: 14 }}>Products</Link>
            <Text size={14} color="dimmed" lineClamp={1}>{product.title}</Text>
          </Breadcrumbs>
        </Box>

        <Grid gutter={isMobileScreen ? 0 : 'xl'}>
          {/* Image Section */}
          <Grid.Col sm={6}>
            <Paper className={classes.imageCard}>
              {isMobileScreen ? (
                <Box pos="relative">
                  <div className={classes.mobileHeaderActions}>
                    <div style={{ flex: 1 }} />
                    <ActionIcon className={classes.actionButton}>
                      <IconShare size={20} color="black" />
                    </ActionIcon>
                  </div>
                  <Carousel 
                    withIndicators 
                    height={350} 
                    styles={{ indicator: { backgroundColor: theme.colors.brand[5] } }}
                  >
                    {images.map((img) => (
                      <Carousel.Slide key={img.id}>
                        <Image src={img.image} height={350} fit="contain" p="md" />
                      </Carousel.Slide>
                    ))}
                  </Carousel>
                </Box>
              ) : (
                <Grid gutter="xs" p="md">
                  <Grid.Col span={2}>
                    <Flex direction="column" gap="xs">
                      {images.map((img) => (
                        <Image
                          key={img.id}
                          src={img.image}
                          height={70}
                          fit="contain"
                          radius="md"
                          sx={{ 
                            border: `2px solid ${selectedImage === img.image ? theme.colors.brand[5] : theme.colors.gray[2]}`,
                            cursor: 'pointer'
                          }}
                          onClick={() => setSelectedImage(img.image)}
                        />
                      ))}
                    </Flex>
                  </Grid.Col>
                  <Grid.Col span={10}>
                    <Image src={selectedImage} height={450} fit="contain" radius="lg" />
                  </Grid.Col>
                </Grid>
              )}
            </Paper>
          </Grid.Col>

          {/* Info Section */}
          <Grid.Col sm={6}>
            <Paper className={classes.infoCard}>
              <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}>
                <Flex justify="space-between" align="flex-start">
                  <Box flex={1}>
                    <Text size="xs" color="brand.7" fw={700} transform="uppercase" mb={4}>
                      {product.category_name || 'Premium Product'}
                    </Text>
                    <Text fw={700} size={isMobileScreen ? 24 : 32} mb={8} lh={1.2}>
                      {product.title}
                    </Text>
                    <Group spacing="xs" mb={16}>
                      <Rating value={product.review} readOnly size="xs" />
                      <Text size="xs" color="dimmed">({product.product_review?.length || 0} reviews)</Text>
                    </Group>
                  </Box>
                  {!isMobileScreen && (
                    <ActionIcon size="xl" radius="xl" variant="light" color="gray">
                      <IconShare size={20} />
                    </ActionIcon>
                  )}
                </Flex>

                {freshDays !== null && (
                  <Badge 
                    leftSection={<IconClock size={12} />} 
                    color={freshDays <= 3 ? 'red' : freshDays <= 7 ? 'orange' : 'teal'}
                    variant="filled"
                    mb={20}
                  >
                    Best Before: {freshDays} days
                  </Badge>
                )}

                <Text color="dimmed" size="sm" mb={24} lineClamp={3}>
                  {product.short_description}
                </Text>

                {product.isVarient && (
                  <Box mb={24}>
                    <Text fw={600} size="sm" mb={12}>Select Variant</Text>
                    <Group spacing="sm">
                      {varientOptions.map((v) => (
                        <div
                          key={v}
                          className={cx(classes.variantChip, { [classes.selectedVariant]: selectedVarient === v })}
                          onClick={() => {
                            setSelectedVarient(v);
                            const currentTotal = qtyCalculator(quantity, v, product?.varientUnit, product?.isVarient);
                            const availableStock = product.stock_qty - (product.ordered_qty || 0);
                            if (availableStock - currentTotal < (product.min_qty || 0)) {
                              const unitWeight = qtyCalculator(1, v, product?.varientUnit, product?.isVarient);
                              const maxQty = Math.floor((availableStock - (product.min_qty || 0)) / unitWeight);
                              setQuantity(Math.max(1, maxQty));
                            }
                          }}
                        >
                          {product.varientUnit === 'kg' && v < 1 ? v * 1000 + 'g' : v + ` ${product.varientUnit}`}
                        </div>
                      ))}
                    </Group>
                  </Box>
                )}

                <Box className={classes.priceSection}>
                  <Flex align="baseline" gap="md">
                    <Text fw={800} size={28} color="brand.8">₹{totalPrice.toFixed(2)}</Text>
                    <Text strikethrough size="md" color="red.6" opacity={0.8}>₹{totalMrp.toFixed(2)}</Text>
                    <Badge color="red" variant="filled">
                      {Math.round(((totalMrp - totalPrice) / totalMrp) * 100)}% OFF
                    </Badge>
                  </Flex>

                  {batchBreakdown.length > 1 && (
                    <Box className={classes.batchBreakdown}>
                      <Group spacing={4} mb={8}>
                        <IconInfoCircle size={14} color={theme.colors.brand[6]} />
                        <Text size="xs" fw={700} color="brand.8">Price breakdown for {quantity} items:</Text>
                      </Group>
                      {batchBreakdown.map((item, idx) => (
                        <Flex key={idx} justify="space-between" align="center" mb={4}>
                          <Text size="xs" color="gray.7">
                            Batch {idx + 1}: {parseFloat(item.displayQty.toFixed(2))} units @ ₹{item.selling_price} (MRP: ₹{item.mrp})
                          </Text>
                          <Text size="xs" fw={700}>₹{parseFloat((item.appliedQty * item.selling_price).toFixed(2))}</Text>
                        </Flex>
                      ))}
                    </Box>
                  )}
                </Box>

                <Grid mt={30} gutter="md">
                  <Grid.Col span={6}>
                    <Flex align="center" gap="sm">
                      <ThemeIcon variant="light" color="blue" radius="md">
                        <IconCash size={18} />
                      </ThemeIcon>
                      <Box>
                        <Text size="xs" fw={700}>COD</Text>
                        <Text size="xs" color="dimmed">{product.cod ? 'Available' : 'No'}</Text>
                      </Box>
                    </Flex>
                  </Grid.Col>
                </Grid>

                {/* Qty + Add to Cart — inline only for desktop */}
                {!isMobileScreen && (
                  <div className={classes.cartRow}>
                    <div className={classes.qtySelector}>
                      <button
                        className={classes.qtyBtn}
                        onClick={() => setQuantity(q => Math.max(1, q - 1))}
                        disabled={outOfStock}
                      >
                        <IconMinus size={15} />
                      </button>
                      <Text fw={700} size={18} w={36} align="center" lh={1}>
                        {quantity}
                      </Text>
                      <button
                        className={classes.qtyBtn}
                        onClick={() => {
                          const nextQty = qtyCalculator(quantity + 1, selectedVarient, product?.varientUnit, product?.isVarient);
                          const availableStock = product.stock_qty - (product.ordered_qty || 0);
                          if (availableStock - nextQty >= (product.min_qty || 0)) setQuantity(q => q + 1);
                          else showErrorToast({ title: 'Limit', message: 'Out of stock' });
                        }}
                        disabled={outOfStock}
                      >
                        <IconPlus size={15} />
                      </button>
                    </div>

                    <button
                      className={classes.addToCartBtn}
                      disabled={outOfStock}
                      onClick={addToCart}
                    >
                      <IconShoppingCartPlus size={20} />
                      {outOfStock ? 'Sold Out' : 'Add to Cart'}
                    </button>
                  </div>
                )}
              </motion.div>
            </Paper>
          </Grid.Col>
        </Grid>



        {/* Sticky Bottom Bar for Mobile */}
        {isMobileScreen && (
          <motion.div
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            className={classes.stickyBottom}
          >
            <div className={classes.mobileStickyQty}>
              <ActionIcon
                variant="transparent"
                size="lg"
                onClick={() => setQuantity(q => Math.max(1, q - 1))}
                disabled={outOfStock}
              >
                <IconMinus size={16} />
              </ActionIcon>
              <Text fw={700} w={30} align="center">{quantity}</Text>
              <ActionIcon
                variant="transparent"
                size="lg"
                onClick={() => {
                  const nextQty = qtyCalculator(quantity + 1, selectedVarient, product?.varientUnit, product?.isVarient);
                  const availableStock = product.stock_qty - (product.ordered_qty || 0);
                  if (availableStock - nextQty >= (product.min_qty || 0)) setQuantity(q => q + 1);
                  else showErrorToast({ title: 'Limit', message: 'Out of stock' });
                }}
                disabled={outOfStock}
              >
                <IconPlus size={16} />
              </ActionIcon>
            </div>
            <Button
              fullWidth
              size="lg"
              radius="md"
              leftIcon={<IconShoppingCartPlus size={20} />}
              disabled={outOfStock}
              onClick={addToCart}
              sx={{ flex: 1, height: 48 }}
            >
              {outOfStock ? 'Sold Out' : 'Add to Cart'}
            </Button>
          </motion.div>
        )}

        {/* Tabs Section */}
        <Paper shadow="xs" radius="lg" p="xl" mt="xl" mb={isMobileScreen ? 120 : 50}>
          <Tabs defaultValue="description" variant="pills" color="brand">
            <Tabs.List grow={isMobileScreen}>
              <Tabs.Tab value="description">Description</Tabs.Tab>
              <Tabs.Tab value="reviews">Reviews ({product.product_review?.length || 0})</Tabs.Tab>
              <Tabs.Tab value="specs">Specifications</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="description" pt="xl">
              <Text lh={1.8} size="sm">{product.long_description || 'No description available.'}</Text>
            </Tabs.Panel>

            <Tabs.Panel value="reviews" pt="xl">
              <Flex justify="space-between" align="center" mb="xl">
                <Text fw={700}>Customer Reviews</Text>
                <Button variant="outline" size="xs" onClick={() => setIsModalVisible(true)}>Write a Review</Button>
              </Flex>
              {product.product_review?.map((rev, i) => (
                <Box key={i} mb="lg" p="md" sx={{ backgroundColor: theme.colors.gray[0], borderRadius: theme.radius.md }}>
                  <Group position="apart" mb={8}>
                    <Rating value={rev.star} readOnly size="xs" />
                    <Text size="xs" color="dimmed">{rev.user}</Text>
                  </Group>
                  <Text fw={600} size="sm" mb={4}>{rev.title}</Text>
                  <Text size="xs" color="gray.7">{rev.description}</Text>
                </Box>
              ))}
            </Tabs.Panel>

            <Tabs.Panel value="specs" pt="xl">
              <Table verticalSpacing="md">
                <tbody>
                  {specifications.map((s, i) => (
                    <tr key={i}>
                      <td style={{ width: 140 }}><Text fw={700} size="xs" color="dimmed">{s.title}</Text></td>
                      <td><Text size="xs">{s.description}</Text></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Tabs.Panel>
          </Tabs>
        </Paper>
      </Container>

      {/* Review Modal */}
      <Modal opened={isModalVisible} onClose={() => setIsModalVisible(false)} title="Product Review" centered radius="lg">
        <Box p="md">
          <Text size="sm" mb={4}>Rating</Text>
          <Rate allowHalf value={stars} onChange={setStars} />
          <Input placeholder="Review Title" value={reviewTitle} onChange={(e) => setReviewTitle(e.target.value)} style={{ marginTop: 16 }} />
          <Textarea placeholder="Describe your experience" value={reviewText} onChange={(e) => setReviewText(e.target.value)} minRows={4} mt="md" />
          <Button fullWidth mt="xl" radius="md" onClick={submitReview}>Submit Review</Button>
        </Box>
      </Modal>
    </motion.div>
  );
}

export default ProductView;
